/**
 * Lumia — Obaveštenje o kolačićima (<lh-cookie-banner>, sections/cookie-banner.liquid)
 *
 *  - Prikazuje se dok posetilac ne izabere; izbor se čuva u localStorage
 *    ({ analytics, marketing, preferences, version, ts }) pod ključem data-storage-key.
 *  - Ako je dostupan Shopify Customer Privacy API (consent-tracking-api 0.1), saglasnost se
 *    prosleđuje sa setTrackingConsent(); ako je posetilac već odlučio kroz taj API, kartica se ne prikazuje.
 *    Kad sačuvan izbor iste verzije već postoji, API se učitava odloženo (requestIdleCallback / setTimeout),
 *    van startne faze učitavanja — Shopify-jeve skripte čitaju kolačić _tracking_consent direktno.
 *  - [data-cookie-settings] (link „Podešavanja kolačića“ u podnožju) ponovo otvara panel sa podešavanjima.
 *  - Emituje document događaj `cookie-consent:change` { detail: consent }.
 *  - U editoru teme (data-design-mode) prikazuje se samo dok je sekcija izabrana.
 */
(function () {
  'use strict';
  if (window.__lumiaCookieBanner) return;
  window.__lumiaCookieBanner = true;

  var DEFAULT_KEY = 'lh:cookie-consent';

  function readStore(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* privatni režim / blokirano skladište — izbor važi samo za ovu stranu */
    }
  }

  function loadPrivacyApi(cb) {
    var S = window.Shopify;
    // postojeći objekat prihvatamo samo ako je pravi API (ne stub bez setTrackingConsent)
    if (S && S.customerPrivacy && typeof S.customerPrivacy.setTrackingConsent === 'function') {
      return cb(S.customerPrivacy);
    }
    if (S && typeof S.loadFeatures === 'function') {
      try {
        S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
          cb(err ? null : (window.Shopify && window.Shopify.customerPrivacy) || null);
        });
        return;
      } catch (e) {
        /* fall through */
      }
    }
    cb(null);
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function LhCookieBanner() {
    return Reflect.construct(HTMLElement, [], LhCookieBanner);
  }
  LhCookieBanner.prototype = Object.create(HTMLElement.prototype);
  LhCookieBanner.prototype.constructor = LhCookieBanner;
  Object.setPrototypeOf(LhCookieBanner, HTMLElement);

  LhCookieBanner.prototype.connectedCallback = function () {
    if (this.__init) return;
    this.__init = true;

    var self = this;
    this.key = this.getAttribute('data-storage-key') || DEFAULT_KEY;
    this.version = String(this.getAttribute('data-version') || '1');
    this.designMode = this.hasAttribute('data-design-mode');
    this.api = null;
    this.visible = false;

    this.panel = this.querySelector('[data-cookie-settings-panel]');
    this.toggleBtn = this.querySelector('[data-cookie-toggle-settings]');
    this.acceptBtn = this.querySelector('[data-cookie-accept]');
    this.declineBtn = this.querySelector('[data-cookie-decline]');
    this.saveBtn = this.querySelector('[data-cookie-save]');
    this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-cookie-consent]'));

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', function () {
        self.showSettings(self.panel ? self.panel.hidden : false);
      });
    }
    if (this.acceptBtn) {
      this.acceptBtn.addEventListener('click', function () {
        self.save({ analytics: true, marketing: true });
      });
    }
    if (this.declineBtn) {
      this.declineBtn.addEventListener('click', function () {
        self.save({ analytics: false, marketing: false });
      });
    }
    if (this.saveBtn) {
      this.saveBtn.addEventListener('click', function () {
        self.save(self.readInputs(), true);
      });
    }

    this.onDocClick = function (event) {
      var trigger = event.target.closest && event.target.closest('[data-cookie-settings]');
      if (!trigger) return;
      event.preventDefault();
      self.open(true, trigger);
    };
    document.addEventListener('click', this.onDocClick);

    this.onKeydown = function (event) {
      if (event.key === 'Escape' && self.visible && self.hasDecision()) self.hide();
    };
    document.addEventListener('keydown', this.onKeydown);

    if (this.designMode) {
      this.onSectionSelect = function (event) {
        if (event.target && event.target.contains(self)) self.open(false);
      };
      this.onSectionDeselect = function (event) {
        if (event.target && event.target.contains(self)) self.hide();
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    }

    // Ako izbor iste verzije već postoji, sinhronizacija sa Shopify Customer Privacy API-jem nije hitna
    // (wpm/trekkie čitaju kolačić _tracking_consent direktno) — učitaj API tek kad je glavna nit slobodna.
    // U editoru teme i kad nema odluke zadrži postojeći tok (decide() treba currentVisitorConsent odmah).
    if (!this.designMode && this.stored()) {
      var deferred = function () {
        var latest = self.stored(); // ponovo pročitaj: korisnik je u međuvremenu mogao promeniti izbor
        if (latest) self.applyConsent(latest);
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(deferred, { timeout: 4000 });
      } else {
        window.setTimeout(deferred, 2500);
      }
      return;
    }

    loadPrivacyApi(function (api) {
      self.api = api;
      self.decide();
    });
  };

  LhCookieBanner.prototype.disconnectedCallback = function () {
    window.clearTimeout(this._hideTimer);
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onSectionSelect) {
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    }
  };

  LhCookieBanner.prototype.stored = function () {
    var stored = readStore(this.key);
    if (!stored || typeof stored !== 'object') return null;
    if (String(stored.version) !== this.version) return null;
    return stored;
  };

  LhCookieBanner.prototype.hasDecision = function () {
    return !!this.stored();
  };

  /* Prvo učitavanje: primeni sačuvan izbor ili prikaži karticu. */
  LhCookieBanner.prototype.decide = function () {
    if (this.designMode) return; // u editoru samo na shopify:section:select

    var stored = this.stored();
    if (stored) {
      this.applyConsent(stored);
      return;
    }

    // Posetilac je već odlučio kroz Shopify-jev API (npr. drugi baner / prethodna tema)?
    var api = this.api;
    if (api && typeof api.currentVisitorConsent === 'function') {
      try {
        var current = api.currentVisitorConsent() || {};
        var decided = ['analytics', 'marketing', 'preferences'].some(function (k) {
          return current[k] === 'yes' || current[k] === 'no';
        });
        if (decided) {
          writeStore(this.key, {
            analytics: current.analytics === 'yes',
            marketing: current.marketing === 'yes',
            preferences: true,
            version: this.version,
            ts: Date.now(),
            source: 'shopify'
          });
          return;
        }
      } catch (e) {
        /* ignore */
      }
    }

    this.open(false);
  };

  LhCookieBanner.prototype.readInputs = function () {
    var out = { analytics: false, marketing: false };
    this.inputs.forEach(function (input) {
      out[input.getAttribute('data-cookie-consent')] = !!input.checked;
    });
    return out;
  };

  LhCookieBanner.prototype.syncInputs = function () {
    var stored = this.stored() || {};
    this.inputs.forEach(function (input) {
      var k = input.getAttribute('data-cookie-consent');
      input.checked = !!stored[k];
    });
  };

  LhCookieBanner.prototype.showSettings = function (show) {
    if (!this.panel) return;
    this.panel.hidden = !show;
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', show ? 'true' : 'false');
    if (show) {
      var first = this.inputs.length ? this.inputs[0] : this.saveBtn;
      if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
    }
  };

  LhCookieBanner.prototype.open = function (withSettings, opener) {
    var self = this;
    // otkaži odloženo sakrivanje iz hide() ako je panel ponovo otvoren tokom tranzicije
    window.clearTimeout(this._hideTimer);
    this._hideTimer = 0;
    this.opener = opener || null;
    this.syncInputs();
    this.hidden = false;
    this.visible = true;
    // dva okvira da bi tranzicija krenula iz početnog stanja
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        self.classList.add('is-visible');
      });
    });
    if (withSettings) {
      this.showSettings(true);
    } else if (this.panel) {
      this.panel.hidden = true;
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
    }
    // Ne krademo fokus pri prvom prikazu; fokusiramo samo kada je korisnik sam otvorio panel.
    if (opener && this.toggleBtn) {
      var target = withSettings && this.inputs.length ? this.inputs[0] : this.toggleBtn;
      target.focus({ preventScroll: true });
    }
  };

  LhCookieBanner.prototype.hide = function () {
    var self = this;
    if (!this.visible && this.hidden) return;
    this.visible = false;
    this.classList.remove('is-visible');
    window.clearTimeout(this._hideTimer);
    var done = function () {
      self._hideTimer = 0;
      if (self.visible) return; // ponovo otvoren u međuvremenu
      self.hidden = true;
      if (self.opener && document.body.contains(self.opener)) {
        self.opener.focus({ preventScroll: true });
      }
      self.opener = null;
    };
    if (prefersReducedMotion()) done();
    else this._hideTimer = window.setTimeout(done, 320);
  };

  LhCookieBanner.prototype.save = function (choice, notify) {
    var consent = {
      analytics: !!choice.analytics,
      marketing: !!choice.marketing,
      preferences: true,
      version: this.version,
      ts: Date.now(),
      source: 'banner'
    };
    writeStore(this.key, consent);
    this.applyConsent(consent);
    this.hide();
    if (notify) {
      var message = this.getAttribute('data-saved-message');
      if (message && window.Lumia && typeof window.Lumia.toast === 'function') {
        window.Lumia.toast(message, { type: 'success' });
      }
    }
  };

  /* Prosledi izbor Shopify API-ju; isti izbor (ts) se ne šalje dvaput. */
  LhCookieBanner.prototype._pushConsent = function (consent) {
    var api = this.api;
    if (!api || typeof api.setTrackingConsent !== 'function') return;
    if (consent.ts && consent.ts === this._syncedTs) return;
    try {
      api.setTrackingConsent(
        {
          analytics: !!consent.analytics,
          marketing: !!consent.marketing,
          preferences: true,
          sale_of_data: !!consent.marketing
        },
        function () {}
      );
      this._syncedTs = consent.ts;
    } catch (e) {
      /* ignore */
    }
  };

  LhCookieBanner.prototype.applyConsent = function (consent) {
    var self = this;
    if (this.api) {
      this._pushConsent(consent);
    } else {
      // API još nije učitan (odložena sinhronizacija ili save() pre nje): učitaj ga i pošalji
      // najnoviji sačuvan izbor — poslednja odluka uvek pobeđuje.
      loadPrivacyApi(function (api) {
        self.api = api;
        self._pushConsent(self.stored() || consent);
      });
    }
    try {
      document.dispatchEvent(new CustomEvent('cookie-consent:change', { bubbles: true, detail: consent }));
    } catch (e) {
      /* ignore */
    }
  };

  if (window.customElements && !window.customElements.get('lh-cookie-banner')) {
    window.customElements.define('lh-cookie-banner', LhCookieBanner);
  }

  // Editor teme: nova instanca sekcije se sama registruje kroz custom element;
  // pri ponovnom učitavanju sekcije ukloni zaostali flag da bi se listeneri ponovo vezali.
  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target && event.target.querySelector && event.target.querySelector('lh-cookie-banner');
    if (el && el.__init && el.designMode) el.open(false);
  });
})();
